import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Loader2, Crown, CheckCircle2, AlertTriangle } from "lucide-react";
import { confirmLifetimePayment } from "@/server/lifetime.functions";

export const Route = createFileRoute("/dashboard/lifetime-success")({
  validateSearch: (s: Record<string, unknown>) => ({
    session_id: typeof s.session_id === "string" ? s.session_id : "",
    pid: typeof s.pid === "string" ? s.pid : "",
  }),
  component: LifetimeSuccessPage,
});

function LifetimeSuccessPage() {
  const { session_id, pid } = useSearch({ from: "/dashboard/lifetime-success" });
  const confirm = useServerFn(confirmLifetimePayment);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!session_id || !pid) {
      setState("error");
      setError("Missing payment reference. Please contact support.");
      return;
    }
    let cancelled = false;
    confirm({ data: { session_id, provider_id: pid } })
      .then(() => {
        if (!cancelled) setState("ok");
      })
      .catch((err: any) => {
        if (cancelled) return;
        setState("error");
        setError(err?.message ?? "Could not confirm payment.");
      });
    return () => {
      cancelled = true;
    };
  }, [session_id, pid, confirm]);

  return (
    <div>

      <main className="mx-auto max-w-2xl px-6 pt-32 pb-20">
        <div className="card-surface rounded-md p-10 text-center">
          {state === "loading" && (
            <>
              <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />
              <h1 className="mt-5 text-[22px] font-medium tracking-tight">
                Confirming your Lifetime Premium…
              </h1>
              <p className="mt-2 text-[14px] text-muted-foreground">
                One moment while we activate your benefits.
              </p>
            </>
          )}
          {state === "ok" && (
            <>
              <div className="mx-auto h-14 w-14 rounded-md bg-primary/10 flex items-center justify-center">
                <Crown className="h-7 w-7 text-primary" />
              </div>
              <h1 className="mt-5 text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">
                Congratulations!
              </h1>
              <p className="mt-3 text-[14px] text-foreground">
                Your Lifetime Premium access is active. Your profile now includes lifetime
                Premium benefits and 4 featured placement days every month.
              </p>
              <ul className="mt-6 space-y-2 text-left text-sm text-foreground max-w-md mx-auto">
                {[
                  "Lifetime Premium badge applied",
                  "Enhanced profile unlocked",
                  "Appointment request button enabled",
                  "4 featured placement days scheduled this month",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Button asChild>
                  <Link to="/dashboard/doctor" search={{} as any}>Go to dashboard</Link>
                </Button>
              </div>
            </>
          )}
          {state === "error" && (
            <>
              <div className="mx-auto h-14 w-14 rounded-md bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-7 w-7 text-destructive" />
              </div>
              <h1 className="mt-5 text-[22px] font-medium tracking-tight">
                We couldn't confirm your payment
              </h1>
              <p className="mt-3 text-[14px] text-muted-foreground">{error}</p>
              <div className="mt-6 flex justify-center gap-3">
                <Button variant="outline" asChild>
                  <Link to="/dashboard/doctor" search={{} as any}>Back to dashboard</Link>
                </Button>
                <Button asChild>
                  <a href="mailto:contact@heallexa.com">Contact support</a>
                </Button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
